/**
 * In-app updater — orchestration.
 *
 * On a real device: fetch the update manifest, compare it to our own version
 * (src/update/appVersion.ts), and if there's a newer build, let the user download
 * and install it over the top. Everything native is guarded by canInstallApk() so
 * this is a clean no-op on web/dev.
 *
 * Like the chart packs, the manifest itself is fetched through the NATIVE HTTP
 * stack (Filesystem.downloadFile) on device rather than the WebView's fetch. The
 * manifest lives on a GitHub Release, whose asset download redirects to a host
 * that doesn't send CORS headers, so a WebView fetch from https://localhost would
 * be blocked. The native download side-steps that exactly as the pack download
 * does. On web/dev we just use fetch (the dev host serves permissive CORS).
 *
 * The compare/parse logic lives in src/update/updateManifest.ts as pure, tested
 * functions; this store only wires them to the fetch, the download, and the UI.
 */
import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { APP_VERSION } from '@/update/appVersion';
import { UPDATE_MANIFEST_URL } from '@/update/updateConfig';
import {
  parseUpdateManifest,
  decideUpdate,
  type UpdateManifest,
} from '@/update/updateManifest';
import { downloadAndInstallApk, canInstallApk } from '@/update/apkDownload';

/** Where we are in checking for an update. */
export type CheckStatus = 'idle' | 'checking' | 'upToDate' | 'available' | 'error';
/** Where we are in downloading + installing the update. */
export type InstallStatus = 'idle' | 'downloading' | 'installing' | 'error';

const LS_DISMISSED = 'acp.update.dismissedCode.v1';

function loadDismissedCode(): number {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_DISMISSED) : null;
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}
function saveDismissedCode(code: number): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_DISMISSED, String(code));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Fetch + parse the manifest. Throws on a network/read failure (so a manual check
 * can show an error), but returns null for a manifest that loaded and is simply
 * unparseable — the caller treats that the same as "nothing to offer".
 */
async function fetchManifest(url: string): Promise<UpdateManifest | null> {
  let text: string;
  if (Capacitor.isNativePlatform()) {
    const dir = Directory.Cache;
    const path = 'updates/update.json';
    await Filesystem.mkdir({ path: 'updates', directory: dir, recursive: true }).catch(() => {});
    await Filesystem.deleteFile({ path, directory: dir }).catch(() => {});
    await Filesystem.downloadFile({ url, path, directory: dir });
    const res = await Filesystem.readFile({ path, directory: dir, encoding: Encoding.UTF8 });
    text = typeof res.data === 'string' ? res.data : '';
  } else {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`Update manifest ${r.status}`);
    text = await r.text();
  }
  try {
    return parseUpdateManifest(JSON.parse(text) as unknown);
  } catch {
    return null; // loaded but not valid JSON — offer nothing rather than crash
  }
}

interface UpdateState {
  checkStatus: CheckStatus;
  installStatus: InstallStatus;
  /** The latest-build manifest, present once a newer build is found. */
  manifest: UpdateManifest | null;
  /** Download progress 0..1 while installStatus is 'downloading'. */
  progress: number;
  bytes: number;
  total: number;
  error: string | null;
  /** versionCode the user tapped "Not now" on — the startup prompt respects it. */
  dismissedCode: number;

  /** The running app's own version (handy for an About/version line). */
  currentVersionName: string;

  /** True when an update is available AND the user hasn't dismissed this one. */
  shouldPrompt: () => boolean;

  /** Check for a newer build. Safe (no-op'ish) on web/dev. */
  check: () => Promise<void>;
  /** Download the available update and hand it to the system installer. */
  startUpdate: () => Promise<void>;
  /** "Not now": remember this version so the startup prompt doesn't nag. */
  dismiss: () => void;
}

export const useUpdateStore = create<UpdateState>((set, get) => ({
  checkStatus: 'idle',
  installStatus: 'idle',
  manifest: null,
  progress: 0,
  bytes: 0,
  total: 0,
  error: null,
  dismissedCode: loadDismissedCode(),
  currentVersionName: APP_VERSION.versionName,

  shouldPrompt: () => {
    const s = get();
    return (
      s.checkStatus === 'available' &&
      s.manifest != null &&
      s.manifest.versionCode !== s.dismissedCode
    );
  },

  check: async () => {
    // Off-device there's nothing to install, so report up-to-date and stop.
    if (!canInstallApk()) {
      set({ checkStatus: 'upToDate', manifest: null });
      return;
    }
    set({ checkStatus: 'checking', error: null });
    let manifest: UpdateManifest | null;
    try {
      manifest = await fetchManifest(UPDATE_MANIFEST_URL);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not check for updates';
      set({ checkStatus: 'error', error: msg });
      return;
    }
    const decision = decideUpdate(APP_VERSION, manifest);
    if (decision.available && decision.manifest) {
      set({ checkStatus: 'available', manifest: decision.manifest });
    } else {
      set({ checkStatus: 'upToDate', manifest: null });
    }
  },

  startUpdate: async () => {
    const m = get().manifest;
    if (!m || !canInstallApk()) return;
    set({ installStatus: 'downloading', progress: 0, bytes: 0, total: 0, error: null });
    try {
      await downloadAndInstallApk(m.apkUrl, (p) => {
        set({
          bytes: p.bytes,
          total: p.total,
          progress: p.total > 0 ? p.bytes / p.total : 0,
        });
      });
      // The download is done and the OS installer has been launched; it now owns
      // the flow (its own confirm + the one-time "allow from this app" prompt).
      set({ installStatus: 'installing' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Update failed';
      set({ installStatus: 'error', error: msg });
    }
  },

  dismiss: () => {
    const code = get().manifest?.versionCode ?? 0;
    saveDismissedCode(code);
    set({ dismissedCode: code });
  },
}));
